import {
  DataFetcher,
  PriorityConfig,
  useGrpcRequest,
} from '@aboutyou/data-fetcher';
import {CategoryMetaService_GetBreadcrumbsLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/meta/service.grpc.lazy';
import {
  GetBreadcrumbsResponse,
  GetBreadcrumbsResponse_Breadcrumb,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/meta/service.types';
import {Breadcrumbs, mobileOnlyLinaria} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {useAnyFilterFlyoutOpened} from '../../../features/Filters/shared/useAnyFilterFlyoutOpened';
import {useAppliedPotentialFilters} from '../../../features/ProductStream/utils';
import {useCategoryCacheKeyTransformer, useCategoryIdentifier} from '../utils';

const generateItemsList = (
  breadcrumbs: GetBreadcrumbsResponse_Breadcrumb[],
) => {
  return breadcrumbs.map((breadcrumb, index) => {
    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': breadcrumb.path,
        name: breadcrumb.label,
      },
    };
  });
};

const createBreadcrumbsJsonLd = (
  breadcrumbs: GetBreadcrumbsResponse_Breadcrumb[],
) => {
  return JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: generateItemsList(breadcrumbs),
  });
};

export const CategoryBreadcrumbs: FC<PriorityConfig> = ({
  renderingPriority,
}) => {
  return (
    <DataFetcher
      request={useBreadcrumbs()}
      Error={null}
      View={BreadcrumbsView}
      renderingPriority={renderingPriority}
    />
  );
};

const BreadcrumbsView: React.FC<GetBreadcrumbsResponse> = React.memo(
  function BreadcrumbsView({breadcrumbs}) {
    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: createBreadcrumbsJsonLd(breadcrumbs),
          }}
        />
        <StyledBreadcrumbs links={breadcrumbs} />
      </>
    );
  },
);

const useBreadcrumbs = () => {
  const categoryIdentifier = useCategoryIdentifier();
  const categoryCacheKeyTransformer = useCategoryCacheKeyTransformer();
  const appliedFilters = useAppliedPotentialFilters();
  const isFilterFlyoutOpen = useAnyFilterFlyoutOpened();

  return useGrpcRequest(
    CategoryMetaService_GetBreadcrumbsLazy,
    {
      category: {
        identifier: categoryIdentifier,
      },
      appliedFilters: {
        filters: appliedFilters,
        quickFilter: {},
      },
    },
    {
      disabled: isFilterFlyoutOpen,
      retry: false,
      transformCacheKey: categoryCacheKeyTransformer,
      nonEssential: true,
    },
  );
};

const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-bottom: 0.25rem;

  ${mobileOnlyLinaria('margin-left: 1rem')};
`;
